import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Download, FileText, Mic, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { ThreadChat } from '@/components/creative/ChatBox';
import {
  approveRequest, declineRequest, errorText, openRequestFile, openRequestVoice, purgeRequestFile,
  type ClientRequestDetail,
} from '@/lib/requests';
import { cn } from '@/lib/cn';
import { Bi, STATUS } from '@/pages/portal/requests';

const TYPES = ['reel', 'video', 'static', 'carousel', 'story', 'photo', 'copy'];
const FUNNELS = [{ v: 'TOF', l: 'TOF · reach' }, { v: 'MOF', l: 'MOF · trust' }, { v: 'BOF', l: 'BOF · book' }];

type StrategyOpt = { id: string; code: string; title: string; period_start?: string | null; period_end?: string | null;
  slots: { id: string; type: string; funnel: string; open: boolean }[] };

/** One request, for the clinic (portal) and for KAF (staff). Deciding is shown only to deciders. */
export default function RequestDetail({ r, portal, isAdmin = false }: { r: ClientRequestDetail; portal: boolean; isAdmin?: boolean }) {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: portal ? ['portal-request', r.id] : ['client-request', r.id] });
    qc.invalidateQueries({ queryKey: portal ? ['portal-requests'] : ['client-requests'] });
    qc.invalidateQueries({ queryKey: ['thread', 'request', r.id] });   // the decision is posted in the conversation
  };
  const s = STATUS[r.status] ?? STATUS.new;
  const folders = useMemo(() => {
    const m = new Map<string, typeof r.files>();
    for (const f of r.files) m.set(f.folder, [...(m.get(f.folder) || []), f]);
    return [...m.entries()];
  }, [r.files]);
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);

  async function open(fid: string, download = false) {
    try { window.open(await openRequestFile(r.id, fid, download), '_blank', 'noopener'); }
    catch (e) { toast.error(errorText(e, 'Could not open the file')); }
  }
  const purge = useMutation({
    mutationFn: (fid: string) => purgeRequestFile(r.id, fid),
    onSuccess: () => { toast.success('File purged'); refresh(); },
    onError: (e) => toast.error(errorText(e, 'Could not purge')),
  });

  const steps = [
    { k: 'sent', en: 'Sent', ar: 'أُرسل', on: true },
    { k: 'review', en: 'With KAF', ar: 'لدى KAF', on: true },
    { k: 'decided', en: r.status === 'declined' ? 'Not planned' : 'Approved', ar: r.status === 'declined' ? 'تعذّر التنفيذ' : 'تمت الموافقة', on: r.status !== 'new' },
    ...(r.kind === 'creative' ? [{ k: 'prod', en: 'In production', ar: 'قيد التنفيذ', on: r.status === 'converted' }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="bg-bg-card border border-bg-card rounded-2xl p-4 md:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-semibold text-muted-strong" dir="auto">{r.title}</h1>
            <div className="text-[13px] text-muted mt-0.5">
              {r.kind === 'creative' ? 'Creative request · طلب محتوى' : 'General request · طلب عام'}
              {!portal && r.client_name && <> · <span className="text-muted-strong">{r.client_name}</span></>}
              {' · '}{r.requested_by_name} · {new Date(r.created_at).toLocaleString()}
            </div>
          </div>
          <span className={cn('text-[12px] px-2 py-0.5 rounded-full', s.tone)} data-testid="request-status">{s.en} · <span lang="ar">{s.ar}</span></span>
        </div>

        <ol className="flex items-center gap-1.5 flex-wrap text-[12px]" aria-label="Progress">
          {steps.map((st, i) => (
            <li key={st.k} className="flex items-center gap-1.5">
              <span className={cn('px-2 py-0.5 rounded-full border', st.on ? 'border-emerald-500/50 text-emerald-300' : 'border-line text-muted')}>{st.en} · <span lang="ar">{st.ar}</span></span>
              {i < steps.length - 1 && <span className="text-muted">›</span>}
            </li>
          ))}
        </ol>

        {r.brief && <p className="text-sm text-muted-strong whitespace-pre-wrap leading-relaxed" dir="auto">{r.brief}</p>}
        {r.has_voice && (
          <div className="flex items-center gap-2">
            {voiceUrl ? <audio controls src={voiceUrl} className="h-10" /> : (
              <button onClick={async () => { try { setVoiceUrl(await openRequestVoice(r.id)); } catch (e) { toast.error(errorText(e, 'Could not play')); } }}
                className="min-h-11 px-3 rounded-lg border border-line text-sm text-muted-strong inline-flex items-center gap-2">
                <Mic size={14} /> Play the voice note · <span lang="ar">تشغيل الرسالة الصوتية</span>
              </button>
            )}
          </div>
        )}
        {r.status === 'converted' && r.converted_creative_code && (
          <p className="text-[13px] text-emerald-400">
            In production as {portal ? r.converted_creative_code : <Link className="underline" to={`/creatives/${r.converted_creative_id}`}>{r.converted_creative_code}</Link>}
            {r.strategy_code && <> on {r.strategy_code}</>}.
          </p>
        )}
        {r.action_note && r.status !== 'new' && (
          <p className="text-[13px] text-emerald-300 whitespace-pre-wrap" dir="auto" data-testid="action-note">
            What KAF will do · <span lang="ar">ما سيقوم به فريق KAF</span>: {r.action_note}
          </p>
        )}
        {r.decline_reason && <p className="text-[13px] text-muted" dir="auto">Not planned: {r.decline_reason}</p>}
      </div>

      {folders.length > 0 && (
        <div className="bg-bg-card border border-bg-card rounded-2xl p-4 space-y-3" data-testid="request-files">
          <div className="text-sm font-semibold text-muted-strong"><Bi en="Files" ar="الملفات" /></div>
          {folders.map(([f, list]) => (
            <div key={f}>
              <div className="text-[12px] uppercase tracking-wide text-muted mb-1">{f} ({list.length})</div>
              <ul className="divide-y divide-slate-800/60">
                {list.map((x) => (
                  <li key={x.id} className="flex items-center gap-2 py-1.5 text-[13px]">
                    <FileText size={14} className="text-muted shrink-0" />
                    {x.status === 'purged'
                      ? <span className="flex-1 text-muted line-through" dir="auto">{x.name} (removed)</span>
                      : <button onClick={() => open(x.id)} className="flex-1 text-start text-muted-strong hover:text-accent-400 truncate min-h-9" dir="auto">{x.name}</button>}
                    {x.size ? <span className="text-muted tabular-nums">{(x.size / 1048576).toFixed(1)} MB</span> : null}
                    {x.status !== 'purged' && (
                      <button onClick={() => open(x.id, true)} aria-label={`Download ${x.name}`} className="h-9 w-9 inline-flex items-center justify-center text-muted hover:text-muted-strong"><Download size={14} /></button>
                    )}
                    {isAdmin && !portal && x.status !== 'purged' && (
                      <button onClick={() => { if (window.confirm(`Delete ${x.name} for good? It is also removed from the creative.`)) purge.mutate(x.id); }}
                        aria-label={`Purge ${x.name}`} className="h-9 w-9 inline-flex items-center justify-center text-muted hover:text-rose-400"><Trash2 size={14} /></button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {r.consent_by_name && <p className="text-[12px] text-muted">Consent confirmed by {r.consent_by_name}{r.consent_at ? ` on ${new Date(r.consent_at).toLocaleDateString()}` : ''}.</p>}
        </div>
      )}

      {!portal && r.can_decide && r.status === 'new' && <Decide r={r} onDone={refresh} />}

      <ThreadChat kind="request" id={r.id} defaultOpen tall />
    </div>
  );
}

function monthCovers(s: StrategyOpt, today: string): boolean {
  return !!s.period_start && s.period_start <= today && (!s.period_end || s.period_end >= today);
}

function Decide({ r, onDone }: { r: ClientRequestDetail; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'approve' | 'decline'>('idle');
  const [note, setNote] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const strategies = (r.strategies || []) as StrategyOpt[];
  const [strategyId, setStrategyId] = useState(() => (strategies.find((s) => monthCovers(s, today)) ?? strategies[0])?.id ?? '');
  const [type, setType] = useState('reel');
  const [funnel, setFunnel] = useState('TOF');
  const [confirming, setConfirming] = useState(false);
  const chosen = strategies.find((s) => s.id === strategyId);
  const slot = chosen?.slots.find((sl) => sl.open && sl.type === type && sl.funnel === funnel);

  const approve = useMutation({
    mutationFn: () => approveRequest(r.id, r.kind === 'general' ? { note } : { note: note || undefined, strategy_id: strategyId, creative_type: type, funnel_stage: funnel }),
    onSuccess: () => { toast.success(r.kind === 'general' ? 'Approved. The clinic is told.' : 'Approved. The creative is on the plan.'); onDone(); },
    onError: (e) => toast.error(errorText(e, 'Could not approve')),
  });
  const decline = useMutation({
    mutationFn: () => declineRequest(r.id, note),
    onSuccess: () => { toast.success('Declined. The clinic is told.'); onDone(); },
    onError: (e) => toast.error(errorText(e, 'Could not decline')),
  });

  return (
    <div className="bg-bg-card border border-accent-400/30 rounded-2xl p-4 space-y-3" data-testid="decide">
      {mode === 'idle' && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setMode('approve')} data-testid="decide-approve" className="min-h-11 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium inline-flex items-center gap-2"><Check size={14} /> Approve</button>
          <button onClick={() => setMode('decline')} data-testid="decide-decline" className="min-h-11 px-4 rounded-lg border border-line text-sm text-muted inline-flex items-center gap-2"><X size={14} /> Decline</button>
          <span className="text-[12px] text-muted self-center">Need more from the clinic? Ask in the conversation below.</span>
        </div>
      )}
      {mode === 'approve' && r.kind === 'creative' && (
        strategies.length === 0 ? (
          <p className="text-sm text-amber-300">This clinic has no strategy to add it to. Create this month's strategy first, then approve.</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <label className="text-[13px] text-muted space-y-1">Strategy
                <select value={strategyId} onChange={(e) => { setStrategyId(e.target.value); setConfirming(false); }} data-testid="approve-strategy"
                  className="w-full min-h-11 px-2 rounded-lg border border-line bg-bg text-sm text-muted-strong">
                  {strategies.map((s) => <option key={s.id} value={s.id}>{s.code} · {s.title}{monthCovers(s, today) ? ' (this month)' : ''}</option>)}
                </select>
              </label>
              <label className="text-[13px] text-muted space-y-1">Type
                <select value={type} onChange={(e) => { setType(e.target.value); setConfirming(false); }} data-testid="approve-type" className="w-full min-h-11 px-2 rounded-lg border border-line bg-bg text-sm text-muted-strong">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="text-[13px] text-muted space-y-1">Funnel
                <select value={funnel} onChange={(e) => { setFunnel(e.target.value); setConfirming(false); }} data-testid="approve-funnel" className="w-full min-h-11 px-2 rounded-lg border border-line bg-bg text-sm text-muted-strong">
                  {FUNNELS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                </select>
              </label>
            </div>
            <p className="text-[13px] text-muted" data-testid="approve-slot">
              {slot ? <>It takes open slot <b className="text-muted-strong">{slot.id}</b> ({funnel} · {type}).</> : <>No open {funnel} {type} slot: it is added as an <b className="text-muted-strong">Extra</b> slot.</>}
            </p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} dir="auto" placeholder="Optional: what you will do, for the clinic"
              className="w-full px-3 py-2 rounded-lg border border-line bg-bg text-sm text-muted-strong" />
            {!confirming ? (
              <button onClick={() => setConfirming(true)} disabled={!strategyId} className="min-h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">Continue</button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] text-muted-strong">Make a {type} on {chosen?.code} ({slot ? slot.id : 'Extra'})? The content creator gets the brief.</span>
                <button onClick={() => approve.mutate()} disabled={approve.isPending} data-testid="approve-confirm" className="min-h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">Yes, approve</button>
                <button onClick={() => setConfirming(false)} className="min-h-11 px-3 text-sm text-muted">Back</button>
              </div>
            )}
          </div>
        )
      )}
      {mode === 'approve' && r.kind === 'general' && (
        <div className="space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} dir="auto" placeholder="Exactly what we will do, and by when. The clinic reads this." data-testid="approve-note"
            className="w-full px-3 py-2 rounded-lg border border-line bg-bg text-sm text-muted-strong" />
          <button onClick={() => approve.mutate()} disabled={note.trim().length < 3 || approve.isPending} data-testid="approve-confirm"
            className="min-h-11 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">Approve and tell the clinic</button>
        </div>
      )}
      {mode === 'decline' && (
        <div className="space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} dir="auto" placeholder="Why, in a sentence the clinic will read"
            className="w-full px-3 py-2 rounded-lg border border-line bg-bg text-sm text-muted-strong" />
          <button onClick={() => decline.mutate()} disabled={note.trim().length < 3 || decline.isPending} className="min-h-11 px-4 rounded-lg bg-rose-600 text-white text-sm font-medium disabled:opacity-50">Decline</button>
        </div>
      )}
      {mode !== 'idle' && <button onClick={() => { setMode('idle'); setConfirming(false); }} className="text-[13px] text-muted min-h-9">Cancel</button>}
    </div>
  );
}
